import { logger } from '../lib/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, Menu, LogOut, Home, ClipboardList, BarChart3, Newspaper, Download,
  Eye, UserRound, TrendingUp, CalendarDays, Smartphone, Monitor, Tablet, Globe, Bot
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEVICE_META = {
  celular: { label: 'Celular', icon: Smartphone, color: 'text-green-400' },
  computador: { label: 'Computador', icon: Monitor, color: 'text-blue-400' },
  tablet: { label: 'Tablet', icon: Tablet, color: 'text-purple-400' },
};

const PERIODS = [
  { days: 7, label: '7 dias' },
  { days: 30, label: '30 dias' },
  { days: 90, label: '90 dias' },
];

const StatCard = ({ icon: Icon, iconClass, borderClass, tag, value, label, testId }) => (
  <div className={`glass-dark p-6 rounded-xl border ${borderClass}`} data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <Icon className={`w-8 h-8 ${iconClass}`} />
      <span className="text-xs text-gray-400">{tag}</span>
    </div>
    <div className="text-3xl font-bold text-white mb-1">{value}</div>
    <div className="text-sm text-gray-400">{label}</div>
  </div>
);

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [dlStats, setDlStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const fetchStats = useCallback(async (period) => {
    try {
      const [visitsRes, dlRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/stats?days=${period}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/downloads/stats?days=${period}`, { withCredentials: true }),
      ]);
      setStats(visitsRes.data);
      setDlStats(dlRes.data);
    } catch (error) {
      logger.error('Erro ao buscar estatísticas de visitas:', error);
      toast({ title: 'Erro ao carregar dados', description: 'Não foi possível carregar as visitas.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await axios.get(`${BACKEND_URL}/api/admin/verify`, { withCredentials: true });
        fetchStats(days);
      } catch (error) {
        navigate('/admin/login');
      }
    };
    verifyAuth();
    // eslint-disable-next-line
  }, [navigate]);

  const handlePeriod = (d) => {
    setDays(d);
    fetchStats(d);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  const maxVisitas = Math.max(1, ...(stats?.por_dia || []).map((d) => d.visitas));
  const totalDispositivos = (stats?.dispositivos || []).reduce((s, d) => s + d.visitas, 0) || 1;
  const maxPagina = Math.max(1, ...(stats?.top_paginas || []).map((p) => p.visitas));

  return (
    <div className="min-h-screen bg-black flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 border-r border-gray-800 transition-all duration-300`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-8">
            {sidebarOpen && <h1 className="text-xl font-bold text-yellow-400">WING ADMIN</h1>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white p-2" data-testid="analytics-sidebar-toggle">
              <Menu className="w-6 h-6" />
            </button>
          </div>
          <AdminNav active="analytics" navigate={navigate} sidebarOpen={sidebarOpen} onLogout={handleLogout} />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Visitas do Site</h1>
              <p className="text-gray-400">Acessos registrados diretamente pelo site (LGPD-friendly)</p>
            </div>
            <div className="flex gap-2" data-testid="period-selector">
              {PERIODS.map((p) => (
                <button
                  key={p.days}
                  onClick={() => handlePeriod(p.days)}
                  data-testid={`period-${p.days}-btn`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    days === p.days ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard icon={Eye} iconClass="text-yellow-400" borderClass="border-yellow-500/20" tag="HOJE" value={stats?.visitas_hoje ?? 0} label="Visitas Hoje" testId="stat-visitas-hoje" />
            <StatCard icon={TrendingUp} iconClass="text-green-400" borderClass="border-green-500/20" tag={`${days} DIAS`} value={stats?.visitas_periodo ?? 0} label="Visitas no Período" testId="stat-visitas-periodo" />
            <StatCard icon={UserRound} iconClass="text-blue-400" borderClass="border-blue-500/20" tag={`${days} DIAS`} value={stats?.visitantes_unicos ?? 0} label="Visitantes Únicos" testId="stat-visitantes-unicos" />
            <StatCard icon={CalendarDays} iconClass="text-purple-400" borderClass="border-purple-500/20" tag="MÉDIA" value={stats?.media_dia ?? 0} label="Visitas por Dia" testId="stat-media-dia" />
          </div>

          {/* Gráfico de visitas por dia */}
          <div className="glass-dark p-6 rounded-xl border border-white/10 mb-8" data-testid="daily-visits-chart">
            <h2 className="text-xl font-bold text-white mb-6">Visitas por Dia</h2>
            {stats?.visitas_periodo === 0 ? (
              <p className="text-gray-400 text-center py-8">Nenhuma visita registrada ainda. Os acessos ao site aparecerão aqui.</p>
            ) : (
              <>
                <div className="flex items-end gap-[3px] h-48">
                  {(stats?.por_dia || []).map((d) => (
                    <div
                      key={d.date}
                      className="flex-1 group relative flex flex-col justify-end h-full"
                      title={`${d.label}: ${d.visitas} visitas (${d.unicos} únicos)`}
                    >
                      <div
                        className="w-full rounded-t bg-yellow-500 hover:bg-yellow-400 transition-colors min-h-[2px]"
                        style={{ height: `${(d.visitas / maxVisitas) * 100}%` }}
                      />
                      <div className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10 border border-gray-700">
                        {d.label}: <span className="text-yellow-400 font-bold">{d.visitas}</span> ({d.unicos} únicos)
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>{stats?.por_dia?.[0]?.label}</span>
                  <span>{stats?.por_dia?.[stats.por_dia.length - 1]?.label}</span>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Páginas mais visitadas */}
            <div className="glass-dark p-6 rounded-xl border border-white/10" data-testid="top-pages-card">
              <h2 className="text-lg font-bold text-white mb-4">Páginas Mais Visitadas</h2>
              {(stats?.top_paginas || []).length === 0 ? (
                <p className="text-gray-400 text-sm py-4">Sem dados ainda.</p>
              ) : (
                <div className="space-y-3">
                  {stats.top_paginas.map((p) => (
                    <div key={p.path}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-300 truncate mr-2">{p.path === '/' ? '/ (Home)' : p.path}</span>
                        <span className="text-yellow-400 font-bold">{p.visitas}</span>
                      </div>
                      <div className="h-1.5 bg-gray-800 rounded">
                        <div className="h-full bg-yellow-500 rounded" style={{ width: `${(p.visitas / maxPagina) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Dispositivos */}
            <div className="glass-dark p-6 rounded-xl border border-white/10" data-testid="devices-card">
              <h2 className="text-lg font-bold text-white mb-4">Dispositivos</h2>
              {(stats?.dispositivos || []).length === 0 ? (
                <p className="text-gray-400 text-sm py-4">Sem dados ainda.</p>
              ) : (
                <div className="space-y-4">
                  {stats.dispositivos.map((d) => {
                    const meta = DEVICE_META[d.device] || { label: d.device, icon: Monitor, color: 'text-gray-400' };
                    const Icon = meta.icon;
                    const pct = Math.round((d.visitas / totalDispositivos) * 100);
                    return (
                      <div key={d.device} className="flex items-center gap-3">
                        <Icon className={`w-6 h-6 ${meta.color}`} />
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-300">{meta.label}</span>
                            <span className="text-white font-bold">{pct}%</span>
                          </div>
                          <div className="h-1.5 bg-gray-800 rounded">
                            <div className="h-full bg-green-500 rounded" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <span className="text-xs text-gray-500 w-10 text-right">{d.visitas}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Origem do tráfego */}
            <div className="glass-dark p-6 rounded-xl border border-white/10" data-testid="traffic-sources-card">
              <h2 className="text-lg font-bold text-white mb-4">Origem do Tráfego</h2>
              {(stats?.origens || []).length === 0 ? (
                <p className="text-gray-400 text-sm py-4">Sem dados ainda.</p>
              ) : (
                <div className="space-y-3">
                  {stats.origens.map((o) => (
                    <div key={o.origem} className="flex items-center justify-between py-1.5 border-b border-gray-800 last:border-0">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-400" />
                        <span className="text-gray-300 text-sm capitalize">{o.origem === 'direto' ? 'Acesso Direto' : o.origem}</span>
                      </div>
                      <span className="text-yellow-400 font-bold text-sm">{o.visitas}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Downloads de Arquivos */}
          <div className="glass-dark p-6 rounded-xl border border-white/10 mt-6" data-testid="downloads-stats-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Download className="w-5 h-5 text-yellow-400" /> Downloads de Arquivos
              </h2>
              <div className="flex gap-4 text-sm">
                <span className="text-gray-400">Hoje: <span className="text-white font-bold" data-testid="dl-hoje">{dlStats?.downloads_hoje ?? 0}</span></span>
                <span className="text-gray-400">Período: <span className="text-yellow-400 font-bold" data-testid="dl-periodo">{dlStats?.total_periodo ?? 0}</span></span>
                <span className="text-gray-400">Total geral: <span className="text-white font-bold" data-testid="dl-total">{dlStats?.total_geral ?? 0}</span></span>
              </div>
            </div>
            {(dlStats?.top_arquivos || []).length === 0 ? (
              <p className="text-gray-400 text-sm py-4">Nenhum download registrado no período. Os cliques em "Baixar" na Central de Downloads aparecerão aqui.</p>
            ) : (
              <div className="space-y-3">
                {dlStats.top_arquivos.map((f, i) => {
                  const maxDl = dlStats.top_arquivos[0]?.downloads || 1;
                  return (
                    <div key={`${f.file_name}-${f.versao || i}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-300 truncate mr-2">
                          <span className="text-gray-500 mr-2">#{i + 1}</span>
                          {f.file_name}{f.versao ? ` — ${f.versao}` : ''}
                        </span>
                        <span className="text-yellow-400 font-bold">{f.downloads}</span>
                      </div>
                      <div className="h-1.5 bg-gray-800 rounded">
                        <div className="h-full bg-yellow-500 rounded" style={{ width: `${(f.downloads / maxDl) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
